import React, { useState } from 'react';

const CODE_TEXT = '#e2e4f0';

const Section = ({ id, title, children }) => (
  <section id={id} style={{ marginBottom: '3rem', scrollMarginTop: '56px' }}>
    <h2 style={{
      fontSize: '1.4rem', fontWeight: 700, color: 'var(--accent)',
      borderBottom: '2px solid var(--border)', paddingBottom: '0.5rem', marginBottom: '1.25rem',
    }}>
      {title}
    </h2>
    {children}
  </section>
);

const SubSection = ({ title, children }) => (
  <div style={{ marginBottom: '1.75rem' }}>
    <h3 style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.6rem' }}>
      {title}
    </h3>
    {children}
  </div>
);

const CodeBlock = ({ code, lang = 'javascript' }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div style={{ position: 'relative', background: '#0d0d16', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <span style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'JetBrains Mono, monospace' }}>{lang}</span>
        <button onClick={handleCopy} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', color: copied ? '#43e97b' : '#6b7280', fontSize: '0.72rem', padding: '2px 8px', cursor: 'pointer', fontFamily: 'JetBrains Mono, monospace' }}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '1rem', overflowX: 'auto', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.82rem', lineHeight: 1.65, color: CODE_TEXT, whiteSpace: 'pre' }}>
        <code>{code}</code>
      </pre>
    </div>
  );
};

const InfoBox = ({ type = 'info', children }) => {
  const colors = {
    info:    { border: 'var(--accent)',  bg: 'rgba(108,99,255,0.07)', icon: 'ℹ️' },
    tip:     { border: 'var(--accent3)', bg: 'rgba(67,233,123,0.07)', icon: '💡' },
    warning: { border: 'var(--put)',     bg: 'rgba(247,183,49,0.07)', icon: '⚠️' },
  };
  const c = colors[type];
  return (
    <div style={{ border: `1px solid ${c.border}`, background: c.bg, borderRadius: '8px', padding: '0.85rem 1rem', marginBottom: '1rem', fontSize: '0.88rem', lineHeight: 1.6, color: 'var(--text)', display: 'flex', gap: '0.6rem' }}>
      <span style={{ flexShrink: 0 }}>{c.icon}</span>
      <div>{children}</div>
    </div>
  );
};

const IC = ({ children }) => (
  <code style={{ fontFamily: 'JetBrains Mono, monospace', background: 'var(--surface2)', color: 'var(--accent)', padding: '1px 6px', borderRadius: '3px', fontSize: '0.87em', border: '1px solid var(--border)' }}>{children}</code>
);

const LibBadge = ({ name, version, desc }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.7rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--text)', fontFamily: 'JetBrains Mono, monospace' }}>{name}</span>
      {version && <span style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'JetBrains Mono, monospace' }}>v{version}</span>}
    </div>
    <span style={{ fontSize: '0.78rem', color: 'var(--muted)', lineHeight: 1.4 }}>{desc}</span>
  </div>
);

const tocItems = [
  { id: 'overview',        label: 'Overview' },
  { id: 'libraries',       label: 'Key Libraries' },
  { id: 'folder-structure', label: 'Folder Structure' },
  { id: 'request-flow',    label: 'Request Flow' },
  { id: 'routing',         label: 'Routing' },
  { id: 'urlhelper',       label: 'UrlHelper' },
  { id: 'gateway',         label: 'Gateway' },
  { id: 'pages',           label: 'Pages' },
  { id: 'components',      label: 'Components' },
  { id: 'context',         label: 'Global Context' },
  { id: 'conventions',     label: 'Conventions' },
  { id: 'adding-feature',  label: 'Adding a Feature' },
];

export default function ClientStructurePage() {
  const [activeId, setActiveId] = useState('overview');

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActiveId(id); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── Fixed top TOC bar ── */}
      <div style={{ position: 'fixed', top: 0, left: 'var(--sidebar-w)', right: 0, zIndex: 40, background: 'var(--topbar-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', padding: '0 1.5rem', display: 'flex', alignItems: 'center', gap: '0.25rem', overflowX: 'auto', height: '48px' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', marginRight: '0.5rem', fontFamily: 'JetBrains Mono, monospace' }}>Jump to:</span>
        {tocItems.map((item, i) => (
          <React.Fragment key={item.id}>
            {i > 0 && <span style={{ color: 'var(--border)', fontSize: '0.8rem', flexShrink: 0 }}>·</span>}
            <button onClick={() => scrollTo(item.id)} style={{ background: activeId === item.id ? 'rgba(108,99,255,0.15)' : 'none', border: activeId === item.id ? '1px solid rgba(108,99,255,0.4)' : '1px solid transparent', borderRadius: '6px', color: activeId === item.id ? 'var(--accent)' : 'var(--muted)', fontSize: '0.78rem', fontWeight: activeId === item.id ? 600 : 400, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Syne, sans-serif', transition: 'all 0.15s', flexShrink: 0 }}>
              {item.label}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* ── Page content ── */}
      <div style={{ maxWidth: '900px', width: '100%', margin: '0 auto', padding: '2rem 1.5rem', paddingTop: 'calc(48px + 2rem)' }}>

        {/* Header */}
        <div style={{ marginBottom: '2.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '2rem' }}>⚛️</span>
            <h1 style={{ fontSize: '1.9rem', fontWeight: 800, color: 'var(--text)', margin: 0 }}>Frontend (Client) Project Structure</h1>
          </div>
          <p style={{ color: 'var(--muted)', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
            A guide for new team members on how the <strong style={{ color: 'var(--text)' }}>lobees-admin</strong> React application is organized
            and how to add new features following established patterns.
          </p>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            The Lobees admin panel is a <strong style={{ color: 'var(--text)' }}>React 18</strong> single-page application using{' '}
            <strong style={{ color: 'var(--text)' }}>React Router v5</strong> for navigation, <strong style={{ color: 'var(--text)' }}>Material UI v5</strong> for components,
            and <strong style={{ color: 'var(--text)' }}>Bootstrap 5</strong> (via CDN) for layout utilities.
            All HTTP requests go through a central <strong style={{ color: 'var(--text)' }}>Gateway</strong> utility, and all API URLs are declared in a single{' '}
            <strong style={{ color: 'var(--text)' }}>UrlHelper</strong> file.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
            {[
              { icon: '🗺️', label: 'Routes',      desc: 'Centralised route definitions in Routes.js' },
              { icon: '📄', label: 'Pages',        desc: 'Thin wrappers that compose Header + Body + Modals' },
              { icon: '🧩', label: 'Components',   desc: 'Feature-grouped, reusable UI blocks' },
              { icon: '🔗', label: 'UrlHelper',    desc: 'Single source of truth for all API endpoint URLs' },
              { icon: '🌐', label: 'Gateway',      desc: 'Wrapper around axios — never call axios directly' },
              { icon: '🌍', label: 'GlobalContext', desc: 'Shared app state via React Context API' },
            ].map(card => (
              <div key={card.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem' }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{card.icon}</div>
                <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '0.3rem', fontSize: '0.9rem' }}>{card.label}</div>
                <div style={{ color: 'var(--muted)', fontSize: '0.78rem', lineHeight: 1.5 }}>{card.desc}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
            {[
              { label: 'Framework',  value: 'React 18' },
              { label: 'Routing',    value: 'React Router v5' },
              { label: 'UI Library', value: 'MUI v5 + Bootstrap 5' },
              { label: 'HTTP',       value: 'Axios via Gateway' },
              { label: 'State',      value: 'React Context API' },
              { label: 'i18n',       value: 'i18next (EN / ES / CA)' },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginBottom: '0.15rem' }}>{item.label}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--accent)', fontFamily: 'JetBrains Mono, monospace' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </Section>

        {/* Key Libraries */}
        <Section id="libraries" title="Key Libraries">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            The following third-party libraries are used across the admin panel. Prefer these over ad-hoc alternatives so the codebase stays consistent.
          </p>
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>UI &amp; Layout</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="@mui/material" version="5.14" desc="Material UI component library — Button, Dialog, TextField, etc. Theme configured in App.js." />
            <LibBadge name="@mui/icons-material" version="5.14" desc="MUI icon set. Import individual icons to keep bundle size small." />
            <LibBadge name="@mui/x-charts" version="7.x" desc="Official MUI chart components (bar, line, pie). Use for dashboard-level visualisations." />
            <LibBadge name="Bootstrap 5" desc="Loaded via CDN in public/index.html — do NOT npm install. Used for grid, spacing, and flex utility classes." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Data Tables</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="material-react-table" version="1.14" desc="MUI-based data table with sorting, filtering, pagination and row actions. Use this for all admin list views." />
            <LibBadge name="react-pivottable" version="0.11" desc="Pivot-table UI for aggregating and grouping data interactively." />
            <LibBadge name="gantt-task-react" version="0.3" desc="Gantt chart component for project/task timelines." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Rich Text Editors</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
           <LibBadge name="jodit-react" version="4.x" desc="Jodit WYSIWYG editor — lighter alternative to CKEditor for simpler text fields." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Forms &amp; Inputs</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="react-select" version="5.7" desc="Flexible Select/Multi-select with search, async options, and custom renderers. Replaces plain HTML selects." />
            <LibBadge name="react-autosuggest" version="10.x" desc="Autocomplete / typeahead input with custom suggestion rendering." />
            <LibBadge name="react-dropzone" version="14.x" desc="Drag-and-drop file upload zone. Wraps the upload area and exposes onDrop." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Charts &amp; Visualisation</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="ag-charts-community" version="9.x" desc="AG Grid's chart library. Use for advanced interactive charts." />
            <LibBadge name="react-plotly.js" version="2.x" desc="Plotly wrapper for scientific / statistical visualisations." />
            <LibBadge name="react-big-calendar" version="1.x" desc="Full-featured calendar view (month, week, day). Used for event scheduling features." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Drag &amp; Drop</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="react-beautiful-dnd" version="13.x" desc="Fluid drag-and-drop for lists and Kanban boards. Uses DragDropContext + Droppable + Draggable." />
            <LibBadge name="@dnd-kit/core" version="6.x" desc="Modern DnD primitives. Prefer for new features — more flexible than react-beautiful-dnd." />
            <LibBadge name="@dnd-kit/sortable" version="10.x" desc="Sortable preset on top of @dnd-kit/core for ordered lists." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>HTTP, Auth &amp; i18n</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="axios" version="1.4" desc="HTTP client — never import directly in components. Always use Gateway.js wrappers." />
            <LibBadge name="jwt-decode" version="4.x" desc="Decode JWT tokens client-side to read user claims without a server round-trip." />
            <LibBadge name="i18next" version="23.x" desc="Internationalisation framework. Translations in /public/locales/{en,es,ca}." />
            <LibBadge name="react-i18next" version="13.x" desc="React bindings for i18next. Use the useTranslation hook in components." />
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.82rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>Export &amp; File Handling</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <LibBadge name="xlsx" version="0.18" desc="Read and write Excel (.xlsx) files. Used for data export features." />
            <LibBadge name="jspdf" version="3.x" desc="Generate PDF files client-side." />
            <LibBadge name="jspdf-autotable" version="5.x" desc="Table plugin for jsPDF — exports data tables to PDF with minimal code." />
            <LibBadge name="file-saver" version="2.x" desc="Triggers browser file download for blobs (use alongside xlsx / jsPDF)." />
          </div>
          <InfoBox type="warning">
            Bootstrap is loaded via CDN (<IC>{'<link>'}</IC> in <IC>public/index.html</IC>), <strong>not</strong> as an npm package.
            Do not run <IC>npm install bootstrap</IC> — it will conflict with MUI styles.
          </InfoBox>
        </Section>

        {/* Folder Structure */}
        <Section id="folder-structure" title="Folder Structure">
          <CodeBlock lang="folder structure" code={`lobees-admin/src/
│
├── routes/
│   ├── Routes.js          ← All route definitions (public + protected)
│   └── Authmiddleware.js  ← Auth guard: checks localStorage token
│
├── pages/                 ← One folder per feature, PascalCase
│   ├── Dashboard/
│   │   └── Dashboard.js
│   ├── Blog/
│   │   ├── Manage/
│   │   │   └── BlogList.js
│   │   └── Save/
│   │       └── AddUpdateBlog.js
│   ├── CRM/
│   └── ...                (49+ page folders)
│
├── components/            ← Reusable UI, grouped by feature
│   ├── BlogComponents/
│   │   ├── BlogListBody/
│   │   ├── AddUpdateBlogBody/
│   │   ├── Header/
│   │   ├── Modal/
│   │   └── Popup/
│   ├── Common/            ← Shared across all features
│   │   ├── Header/
│   │   ├── BreadCrumb/
│   │   ├── AlertNotification/
│   │   ├── FileUploadField/
│   │   ├── MultiSelectField/
│   │   └── ...            (40+ shared components)
│   └── ...                (39+ feature component folders)
│
├── helper/
│   ├── UrlHelper.js       ← All API base URLs + endpoint paths
│   ├── AuthHelper/
│   ├── TaskHelper/
│   ├── CrmHelper/
│   └── ...                (28+ domain helpers)
│
├── utils/
│   └── Gateway.js         ← Axios wrapper (getData, postData, putData, deleteData)
│
├── context/
│   └── GlobalContext.js   ← App-wide shared state (ecosystems, alerts, etc.)
│
├── Config/
│   └── Config.js          ← Editor configs, react-select styles, UI constants
│
├── locales/               ← Translation JSON files
│   ├── eng/translation.json
│   ├── es/translation.json
│   └── ca/translation.json
│
├── constants/             ← Image exports and app-wide constants
├── data/                  ← Static JSON data (dropdown options, etc.)
└── assets/                ← Images, fonts, CSS`} />

          <InfoBox type="tip">
            Everything follows a strict <strong>feature-per-folder</strong> pattern.
            If you are adding a "Task" feature, you touch: <IC>pages/Tasks/</IC>, <IC>components/TaskComponents/</IC>, and add URLs to <IC>helper/UrlHelper.js</IC>.
          </InfoBox>
        </Section>

        {/* Request Flow */}
        <Section id="request-flow" title="Request Flow">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            Trace a user action (e.g. loading the lead list) through the full stack:
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: '1.5rem' }}>
            {[
              { step: '1', label: 'User navigates to /admin/leads',       detail: 'React Router v5 matches the route → renders LeadList page',                                accent: 'var(--accent)' },
              { step: '2', label: 'Authmiddleware.js',                    detail: 'Checks localStorage.getItem("token") — redirects to login if missing',                    accent: 'var(--put)' },
              { step: '3', label: 'pages/CRM/LeadList.js',                detail: 'Thin page: renders <Header> + <LeadListBody> + modals, sets document.title',             accent: null },
              { step: '4', label: 'components/CrmComponents/LeadListBody', detail: 'Calls getAllLeads() on mount via useEffect',                                              accent: 'var(--accent3)' },
              { step: '5', label: 'helper/UrlHelper.js',                  detail: 'Constructs URL: API_BASE_URL + API_GET_ALL_LEADS + "?token=" + token',                    accent: null },
              { step: '6', label: 'utils/Gateway.js → getData(url)',      detail: 'Sends axios.get(url), returns response.data or null on error',                           accent: 'var(--accent)' },
              { step: '7', label: 'API response',                         detail: '{ status: true, data: [...leads], message: "..." }',                                      accent: 'var(--accent3)' },
              { step: '8', label: 'Component updates state',              detail: 'setLeadList(response.data) → re-renders the table',                                       accent: null },
            ].map((item, idx, arr) => (
              <div key={item.step} style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '32px', flexShrink: 0 }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: item.accent ? item.accent : 'var(--surface2)', color: item.accent ? '#fff' : 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.78rem', fontWeight: 700, border: `2px solid ${item.accent || 'var(--border)'}`, flexShrink: 0 }}>
                    {item.step}
                  </div>
                  {idx < arr.length - 1 && <div style={{ width: '2px', flex: 1, background: 'var(--border)', margin: '2px 0' }} />}
                </div>
                <div style={{ paddingBottom: idx < arr.length - 1 ? '1rem' : 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text)', fontFamily: 'JetBrains Mono, monospace' }}>{item.label}</div>
                  <div style={{ color: 'var(--muted)', fontSize: '0.82rem', marginTop: '0.15rem' }}>{item.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* Routing */}
        <Section id="routing" title="Routing">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            All routes are declared in <IC>src/routes/Routes.js</IC>. The auth guard in{' '}
            <IC>src/routes/Authmiddleware.js</IC> wraps the entire app and decides whether to
            render protected or public routes based on the presence of a JWT token in <IC>localStorage</IC>.
          </p>

          <SubSection title="Authmiddleware.js — Auth Guard">
            <CodeBlock code={`// src/routes/Authmiddleware.js

import { Switch, Route, Redirect } from "react-router-dom";

const Authmiddleware = ({ protectedRoutes, publicRoutes }) => {
  const token = localStorage.getItem("token");

  if (token) {
    return (
      <Switch>
        {protectedRoutes.map((route) => (
          <Route key={route.path} path={route.path} exact component={route.component} />
        ))}
        <Redirect from="/" to="/admin/dashboard" />
      </Switch>
    );
  }

  return (
    <Switch>
      {publicRoutes.map((route) => (
        <Route key={route.path} path={route.path} exact component={route.component} />
      ))}
      <Redirect from="/" to="/" />
    </Switch>
  );
};`} />
          </SubSection>

          <SubSection title="Routes.js — Defining a Route">
            <CodeBlock code={`// src/routes/Routes.js

import LeadList    from "pages/CRM/LeadList";
import AddLead     from "pages/CRM/AddLead";
import Dashboard   from "pages/Dashboard/Dashboard";
import Login       from "pages/Auth/Login/Login";

// ─── Protected routes (require token) ───────────────────────
export const protectedRoutes = [
  { path: "/admin/dashboard",  component: Dashboard },
  { path: "/admin/crm/lead",   component: LeadList  },
  { path: "/admin/crm/lead/save", component: AddLead },
  // ... add your route here
];

// ─── Public routes (no token needed) ────────────────────────
export const publicRoutes = [
  { path: "/",     component: Login  },
  // ...
];`} />
          </SubSection>

          <InfoBox type="warning">
            Always add new pages to <IC>protectedRoutes</IC> unless the page intentionally needs to be
            accessible without login (e.g. a public registration page). Forgot this step = 404 in the browser.
          </InfoBox>
        </Section>

        {/* UrlHelper */}
        <Section id="urlhelper" title="UrlHelper — API URL Registry">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            <IC>src/helper/UrlHelper.js</IC> is the <strong style={{ color: 'var(--text)' }}>single source of truth</strong> for every URL in the app.
            Base server URLs and every API endpoint path are exported as named constants.
            Never hard-code a URL string directly in a component.
          </p>

          <SubSection title="Structure">
            <CodeBlock code={`// src/helper/UrlHelper.js

// ─── Base server URLs ─────────────────────────────────────────
export const API_BASE_URL  = "https://api.saludti.es/api";  // All API calls
export const SERVER_URL    = "https://api.saludti.es/";      // Static files
export const BACKEND_BASE_URL  = "https://admin.saludti.es"; // Admin panel
export const FRONTEND_BASE_URL = "https://web.saludti.es";   // Public front

// Development (uncomment to switch):
// export const API_BASE_URL = "http://localhost:4008/api";
// export const SERVER_URL   = "http://localhost:4008";

// ─── Feature endpoints ────────────────────────────────────────
export const API_CREATE_LEAD     = "/lead/createlead";
export const API_GET_ALL_LEADS   = "/lead/getallleads";
export const API_GET_LEAD_DETAILS = "/lead/getleaddetails";
export const API_UPDATE_LEAD     = "/lead/updatelead";
export const API_DELETE_LEAD     = "/lead/deletelead";`} />
          </SubSection>

          <SubSection title="Using UrlHelper in a Component">
            <CodeBlock code={`// Inside any component or helper file:
import * as url from "helper/UrlHelper";

const token = localStorage.getItem("token");

// GET request — token goes in query string
const requestUrl = url.API_BASE_URL + url.API_GET_ALL_LEADS + \`?token=\${token}\`;

// POST request — token goes in query string on the URL
const postUrl = url.API_BASE_URL + url.API_CREATE_LEAD + \`?token=\${token}\`;`} />
          </SubSection>

          <InfoBox type="tip">
            Naming convention for URL constants: <IC>API_</IC> prefix, then <IC>VERB_SUBJECT</IC> in{' '}
            <IC>UPPER_SNAKE_CASE</IC>. Examples: <IC>API_GET_ALL_LEADS</IC>, <IC>API_UPDATE_COMPANY</IC>,{' '}
            <IC>API_DELETE_BULK_TAGS</IC>. This makes them easy to find with autocomplete.
          </InfoBox>
        </Section>

        {/* Gateway */}
        <Section id="gateway" title="Gateway — HTTP Client">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            <IC>src/utils/Gateway.js</IC> wraps Axios so components never import or call Axios directly.
            This gives one central place to add headers, interceptors, or error handling in the future.
          </p>

          <SubSection title="Available Methods">
            <CodeBlock code={`// src/utils/Gateway.js — exported methods

import { getData, postData, putData, deleteData,
         uploadSingleFile, uploadMultipleFile,
         uploadFormData, uploadSingleMedia } from "utils/Gateway";

// ─── Standard CRUD ────────────────────────────────────────────
getData(url)                  // GET  — returns response.data or null
postData(url, body)           // POST — sends JSON body
putData(url, body)            // PUT  — sends JSON body
deleteData(url)               // DELETE

// ─── File uploads ─────────────────────────────────────────────
uploadSingleFile(url, file)              // single file (field: "upload")
uploadSingleMedia(url, fileObject)       // single media object
uploadMultipleFile(url, fileArray)       // multiple files (field: "uploads")
uploadFormData(url, formValues, method)  // custom FormData fields`} />
          </SubSection>

          <SubSection title="Real Usage Pattern">
            <CodeBlock code={`// In a component (e.g. LeadListBody.js):

import { getData, postData, putData, deleteData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const getAllLeads = async () => {
  try {
    const token = localStorage.getItem("token");
    const requestUrl = url.API_BASE_URL + url.API_GET_ALL_LEADS
                     + \`?token=\${token}\`;

    const response = await getData(requestUrl);

    if (response.status) {
      setLeadList(response.data);
    }
  } catch (error) {
    console.error(error.message);
  }
};

const saveLead = async () => {
  try {
    const token = localStorage.getItem("token");
    const saveUrl = url.API_BASE_URL + url.API_CREATE_LEAD
                  + \`?token=\${token}\`;

    const leadData = { name, email, phone, ecosystemid };
    const response = await postData(saveUrl, leadData);

    if (response.status) {
      setAlertMessage("Lead saved successfully");
      setAlertType("success");
      setShowAlert(true);
    }
  } catch (error) {
    console.error(error.message);
  }
};`} />
          </SubSection>

          <InfoBox type="warning">
            Never <IC>import axios from "axios"</IC> directly in a component or page. Always use Gateway.
            Direct Axios calls bypass any future interceptors (auth refresh, logging, error formatting) that may be added to Gateway.
          </InfoBox>
        </Section>

        {/* Pages */}
        <Section id="pages" title="Pages">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Pages are <strong style={{ color: 'var(--text)' }}>thin orchestration wrappers</strong>. They do almost nothing except:
            compose the <IC>Header</IC>, <IC>Body</IC>, and <IC>Modal</IC> components for that feature,
            set the browser tab title, and pass a <IC>moduleName</IC> prop.
          </p>

          <SubSection title="Pattern">
            <CodeBlock code={`// src/pages/CRM/LeadList.js

import React, { useEffect } from "react";
import Header from "components/Common/Header/Header";
import LeadListBody from "components/CrmComponents/LeadListBody/LeadListBody";
import AddUpdateLeadModal from "components/CrmComponents/Modal/AddUpdateLeadModal";
import DeleteLeadModal from "components/CrmComponents/Modal/DeleteLeadModal";

const LeadList = () => {
  useEffect(() => {
    document.title = "Leads";   // sets the browser tab title
  }, []);

  return (
    <main id="app">
      <Header moduleName="lead" />
      <LeadListBody />
      <AddUpdateLeadModal />
      <DeleteLeadModal />
    </main>
  );
};

export default LeadList;`} />
          </SubSection>

          <SubSection title="Folder Layout for a Feature">
            <CodeBlock lang="folder structure" code={`pages/
└── CRM/
    ├── LeadList.js         ← List view
    ├── AddUpdateLead.js    ← Create / Edit view (optional separate page)
    └── LeadDetails.js      ← Detail / timeline view (if needed)`} />
          </SubSection>

          <InfoBox type="tip">
            All business logic, state, and API calls live in the <strong>Body component</strong>, not in the page.
            The page file should rarely exceed 25 lines.
          </InfoBox>
        </Section>

        {/* Components */}
        <Section id="components" title="Components">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            Components are grouped by feature in <IC>src/components/</IC>. Each feature folder follows
            a consistent internal structure with sub-folders for <IC>Body</IC>, <IC>Header</IC>,{' '}
            <IC>Modal</IC>, <IC>Popup</IC>, and <IC>Tab</IC> where applicable.
          </p>

          <SubSection title="Feature Component Structure">
            <CodeBlock lang="folder structure" code={`components/
└── CrmComponents/                  ← Feature folder (always "<Feature>Components")
    ├── LeadListBody/
    │   └── LeadListBody.js         ← Main list with table, filters, state
    ├── LeadDetailsBody/
    │   └── LeadDetailsBody.js
    ├── Header/
    │   └── LeadHeader.js           ← Feature-specific breadcrumb / actions
    ├── Modal/
    │   ├── AddUpdateLeadModal.js   ← Create & edit in one modal
    │   └── DeleteLeadModal.js
    └── Popup/
        └── LeadFilterPopup.js      ← Advanced filter drawer / popover`} />
          </SubSection>

          <SubSection title="Body Component Pattern">
            <CodeBlock code={`// components/CrmComponents/LeadListBody/LeadListBody.js
/* eslint-disable */
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";

import { GlobalProvider } from "context/GlobalContext";
import { getData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const LeadListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const { ecosystemList } = useContext(GlobalProvider);

  // ── state ──
  const [leadList, setLeadList]     = useState([]);
  const [isLoading, setIsLoading]   = useState(false);
  const [showAlert, setShowAlert]   = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType]   = useState("success");

  // ── data fetch ──
  const getAllLeads = async () => {
    setIsLoading(true);
    try {
      const requestUrl = url.API_BASE_URL + url.API_GET_ALL_LEADS
                       + \`?token=\${token}\`;
      const response = await getData(requestUrl);
      if (response.status) setLeadList(response.data);
    } catch (err) { console.error(err); }
    setIsLoading(false);
  };

  useEffect(() => { getAllLeads(); }, []);

  return (
    <div id="content_wrapper">
      {/* table, modals, alert notification... */}
    </div>
  );
};

export default LeadListBody;`} />
          </SubSection>

          <SubSection title="Common Components (Shared)">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              {[
                { name: 'Common/Header',            desc: 'Top navigation bar with module title, breadcrumb, and action buttons' },
                { name: 'Common/BreadCrumb',        desc: 'Breadcrumb navigation trail — pass breadCrumbText array' },
                { name: 'Common/AlertNotification', desc: 'MUI Snackbar/Alert for success/error feedback' },
                { name: 'Common/FileUploadField',   desc: 'Drag-and-drop or click file upload input' },
                { name: 'Common/MultiSelectField',  desc: 'react-select based multi-select wrapper with consistent styling' },
                { name: 'Common/Modal/*',           desc: 'Reusable modal shells (confirmation, delete, etc.)' },
              ].map(item => (
                <div key={item.name} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem' }}>
                  <IC>{item.name}</IC>
                  <p style={{ color: 'var(--muted)', fontSize: '0.8rem', margin: '0.4rem 0 0', lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </SubSection>
        </Section>

        {/* Global Context */}
        <Section id="context" title="Global Context">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
            <IC>src/context/GlobalContext.js</IC> is the app's shared state layer. It stores data that
            many components need (e.g. ecosystem list, alert triggers, reload flags) and fetches it once
            on app load. Components access it via <IC>useContext(GlobalProvider)</IC>.
          </p>

          <SubSection title="Reading from Context">
            <CodeBlock code={`// In any component:
import { useContext } from "react";
import { GlobalProvider } from "context/GlobalContext";

const LeadListBody = () => {
  const {
    ecosystemList,          // list of ecosystems for dropdowns
    reloadLeadList,         // boolean flag — triggers re-fetch
    setReloadLeadList,      // call this after saving a lead in a modal
    verifyModuleName,       // active module for permission checks
  } = useContext(GlobalProvider);

  // Re-fetch when another component signals a reload
  useEffect(() => {
    if (reloadLeadList) {
      getAllLeads();
      setReloadLeadList(false);
    }
  }, [reloadLeadList]);
};`} />
          </SubSection>

          <SubSection title="Triggering a Cross-Component Reload">
            <CodeBlock code={`// Pattern: Modal saves data → signals parent list to reload

// Inside AddUpdateLeadModal.js (after successful save):
const { setReloadLeadList } = useContext(GlobalProvider);
// ...
if (response.status) {
  setReloadLeadList(true);  // LeadListBody will detect this and re-fetch
  closeModal();
}`} />
          </SubSection>

          <InfoBox type="info">
            Use GlobalContext for state that must be <strong>shared across sibling or distant components</strong>.
            For state that only lives within one component, use local <IC>useState</IC> — don't add it to GlobalContext unnecessarily.
          </InfoBox>
        </Section>

        {/* Conventions */}
        <Section id="conventions" title="Conventions & Rules">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              {
                title: 'Naming',
                items: [
                  'Files & folders: PascalCase (LeadListBody.js)',
                  'Feature component folders: <Feature>Components/',
                  'URL constants: API_VERB_SUBJECT (UPPER_SNAKE_CASE)',
                  'Component functions: PascalCase',
                  'Props & state variables: camelCase',
                ],
              },
              {
                title: 'API Calls',
                items: [
                  'Always import Gateway, never axios directly',
                  'Always import * as url from "helper/UrlHelper"',
                  'Construct URLs: API_BASE_URL + endpoint + ?token=',
                  'Add new endpoints to UrlHelper, never inline',
                  'Check response.status before using response.data',
                ],
              },
              {
                title: 'Pages vs Components',
                items: [
                  'Pages are thin — max ~25 lines',
                  'No API calls or state inside page files',
                  'All logic lives in the Body component',
                  'Always set document.title in useEffect',
                  'Register new page in routes/Routes.js',
                ],
              },
              {
                title: 'State & Context',
                items: [
                  'Local state (useState) for component-only data',
                  'GlobalContext for shared/cross-component state',
                  'Use reload flags to sync list after modal save',
                  'Token always from localStorage.getItem("token")',
                  'Wrap text in t("key") for i18n support',
                ],
              },
            ].map(block => (
              <div key={block.title} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '1rem 1.25rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--accent)', marginBottom: '0.6rem' }}>{block.title}</div>
                <ul style={{ margin: 0, paddingLeft: '1.1rem', color: 'var(--muted)', fontSize: '0.82rem', lineHeight: 1.8 }}>
                  {block.items.map(item => <li key={item}>{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        {/* Adding a Feature */}
        <Section id="adding-feature" title="How to Add a New Feature">
          <p style={{ color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            Follow these steps every time you add a new module (e.g. <em>Tasks</em>):
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {[
              {
                num: '01',
                title: 'Add API URLs to UrlHelper',
                desc: 'Register all endpoint paths before touching any component',
                code: `// src/helper/UrlHelper.js — append at the bottom

/*====== task url start ======*/
export const API_CREATE_TASK      = "/project/addtask";
export const API_GET_ALL_TASKS    = "/project/getalltasks";
export const API_GET_TASK_DETAILS = "/project/gettaskdetails";
export const API_UPDATE_TASK      = "/project/updatetask";
export const API_DELETE_TASK      = "/project/deletetask";
/*====== task url end ======*/`,
              },
              {
                num: '02',
                title: 'Create the Page',
                desc: 'Thin wrapper in src/pages/Tasks/',
                code: `// src/pages/Tasks/TaskList.js

import React, { useEffect } from "react";
import Header from "components/Common/Header/Header";
import TaskListBody from "components/TaskComponents/TaskListBody/TaskListBody";
import AddUpdateTaskModal from "components/TaskComponents/Modal/AddUpdateTaskModal";

const TaskList = () => {
  useEffect(() => { document.title = "Tasks"; }, []);

  return (
    <main id="app">
      <Header moduleName="tasks" />
      <TaskListBody />
      <AddUpdateTaskModal />
    </main>
  );
};

export default TaskList;`,
              },
              {
                num: '03',
                title: 'Create the Body Component',
                desc: 'All logic, API calls, and state live here',
                code: `// src/components/TaskComponents/TaskListBody/TaskListBody.js

import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { MaterialReactTable } from "material-react-table";
import { GlobalProvider } from "context/GlobalContext";
import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const TaskListBody = () => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const { reloadTaskList, setReloadTaskList } = useContext(GlobalProvider);

  const [taskList, setTaskList]   = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAllTasks = async () => {
    setIsLoading(true);
    const requestUrl = url.API_BASE_URL + url.API_GET_ALL_TASKS
                     + \`?token=\${token}\`;
    const response = await getData(requestUrl);
    if (response?.status) setTaskList(response.data);
    setIsLoading(false);
  };

  useEffect(() => { getAllTasks(); }, []);

  useEffect(() => {
    if (reloadTaskList) { getAllTasks(); setReloadTaskList(false); }
  }, [reloadTaskList]);

  const columns = [
    { accessorKey: "title",  header: t("title") },
    { accessorKey: "status", header: t("status") },
  ];

  return (
    <div id="content_wrapper">
      <MaterialReactTable columns={columns} data={taskList} state={{ isLoading }} />
    </div>
  );
};

export default TaskListBody;`,
              },
              {
                num: '04',
                title: 'Register the Route',
                desc: 'Add to protectedRoutes in Routes.js',
                code: `// src/routes/Routes.js

import TaskList from "pages/Tasks/TaskList";

export const protectedRoutes = [
  // ...existing routes...
  { path: "/admin/tasks", component: TaskList },
];`,
              },
              {
                num: '05',
                title: '(Optional) Add Reload Flag to GlobalContext',
                desc: 'Only needed if modals need to signal the list to reload',
                code: `// src/context/GlobalContext.js — add inside the provider

const [reloadTaskList, setReloadTaskList] = useState(false);

// Include in the value object:
<GlobalProvider.Provider value={{
  // ...existing values
  reloadTaskList,
  setReloadTaskList,
}}>

// Then in the modal after a successful save:
const { setReloadTaskList } = useContext(GlobalProvider);
if (response.status) {
  setReloadTaskList(true);
  closeModal();
}`,
              },
            ].map(step => (
              <div key={step.num} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1.25rem', background: 'var(--surface2)', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 800, fontSize: '1.1rem', color: 'var(--accent)' }}>{step.num}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--text)' }}>{step.title}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>{step.desc}</div>
                  </div>
                </div>
                <div style={{ padding: '1rem' }}>
                  <CodeBlock code={step.code} />
                </div>
              </div>
            ))}
          </div>

          <InfoBox type="tip">
            That's the complete cycle for any new client-side feature. When in doubt, find an existing
            similar feature (e.g. <IC>pages/CRM/</IC> + <IC>components/CrmComponents/</IC>) and mirror its structure exactly.
          </InfoBox>
        </Section>

      </div>
    </div>
  );
}
